import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { MapPin, Phone, Mail, Clock, Send, Calendar as CalendarIcon, ChevronLeft, ChevronRight, CheckCircle } from 'lucide-react';
import Layout from '../components/layout/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { API } from '../App';

const ContactPage = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: ''
  });
  const [submitting, setSubmitting] = useState(false);

  // Calendar state - Start with next month if current month has no slots
  const [currentDate, setCurrentDate] = useState(() => {
    const today = new Date();
    // Start with March 2026 since slots start from March
    return new Date(today.getFullYear(), today.getMonth() + 1, 1);
  });
  const [selectedDate, setSelectedDate] = useState(null);
  const [availableSlots, setAvailableSlots] = useState([]);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [allAvailableDates, setAllAvailableDates] = useState([]);

  // Appointment form
  const [appointmentData, setAppointmentData] = useState({
    client_name: '',
    client_email: '',
    client_phone: '',
    subject: '',
    message: ''
  });
  const [bookingSubmitting, setBookingSubmitting] = useState(false);

  const contactInfo = [
    {
      icon: MapPin,
      title: 'Adresse',
      content: 'Commune de Keur Moussa/Tougouny, Thiès, Sénégal',
      link: null
    },
    {
      icon: Phone,
      title: 'Téléphone',
      content: ['+33 7 58 70 96 61', '+221 77 698 49 52', '+221 76 932 65 06'],
      links: ['tel:+33758709661', 'tel:+221776984952', 'tel:+221769326506']
    },
    {
      icon: Mail,
      title: 'Email',
      content: 'contact@sunulex.sn',
      link: 'mailto:contact@sunulex.sn'
    },
    {
      icon: Clock,
      title: 'Horaires',
      content: 'Lun - Ven: 8h - 18h',
      link: null
    }
  ];

  // Calendar helpers
  const daysInMonth = (year, month) => new Date(year, month + 1, 0).getDate();
  const firstDayOfMonth = (year, month) => new Date(year, month, 1).getDay();
  
  const monthNames = [
    'Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin',
    'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'
  ];

  const dayNames = ['Dim', 'Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam'];

  const formatDateString = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const isDateInPast = (date) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return date < today;
  };

  const isWeekend = (date) => {
    const day = date.getDay();
    return day === 0 || day === 6;
  };

  // Fetch all available dates on mount
  useEffect(() => {
    const fetchAllSlots = async () => {
      try {
        const response = await fetch(`${API}/slots`);
        if (response.ok) {
          const data = await response.json();
          const dates = [...new Set(data.map(slot => slot.date))];
          setAllAvailableDates(dates);
        }
      } catch (error) {
        console.error('Error fetching all slots:', error);
      }
    };
    fetchAllSlots();
  }, []);

  // Check if a date has available slots
  const hasAvailableSlots = (date) => {
    const dateStr = formatDateString(date);
    return allAvailableDates.includes(dateStr);
  };

  // Fetch available slots for selected date
  useEffect(() => {
    if (selectedDate) {
      const fetchSlots = async () => {
        setLoadingSlots(true);
        try {
          const dateStr = formatDateString(selectedDate);
          const response = await fetch(`${API}/slots?date=${dateStr}`);
          if (response.ok) {
            const data = await response.json();
            setAvailableSlots(data);
          } else {
            // Generate demo slots if API doesn't have any
            const demoSlots = [
              { slot_id: 'demo-1', time: '09:00', available: true },
              { slot_id: 'demo-2', time: '10:00', available: true },
              { slot_id: 'demo-3', time: '11:00', available: true },
              { slot_id: 'demo-4', time: '14:00', available: true },
              { slot_id: 'demo-5', time: '15:00', available: true },
              { slot_id: 'demo-6', time: '16:00', available: true },
            ];
            setAvailableSlots(demoSlots);
          }
        } catch (error) {
          console.error('Error fetching slots:', error);
          // Generate demo slots on error
          const demoSlots = [
            { slot_id: 'demo-1', time: '09:00', available: true },
            { slot_id: 'demo-2', time: '10:00', available: true },
            { slot_id: 'demo-3', time: '11:00', available: true },
            { slot_id: 'demo-4', time: '14:00', available: true },
            { slot_id: 'demo-5', time: '15:00', available: true },
            { slot_id: 'demo-6', time: '16:00', available: true },
          ];
          setAvailableSlots(demoSlots);
        } finally {
          setLoadingSlots(false);
        }
      };
      fetchSlots();
    }
  }, [selectedDate]);

  // Handle contact form submission
  const handleContactSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      const response = await fetch(`${API}/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        const data = await response.json();
        toast.success(
          <div>
            <p className="font-medium">Message envoyé avec succès !</p>
            <p className="text-sm">Nous vous répondrons rapidement.</p>
          </div>
        );
        setFormData({ name: '', email: '', phone: '', subject: '', message: '' });
        // Redirect to conversation page
        if (data.conversation_id) {
          setTimeout(() => {
            navigate(`/conversation/${data.conversation_id}`);
          }, 1500);
        }
      } else {
        throw new Error('Failed to send message');
      }
    } catch (error) {
      toast.error('Erreur lors de l\'envoi du message. Veuillez réessayer.');
    } finally {
      setSubmitting(false);
    }
  };

  // Handle appointment booking
  const handleBookingSubmit = async (e) => {
    e.preventDefault();
    
    if (!selectedSlot || !selectedDate) {
      toast.error('Veuillez sélectionner une date et un créneau');
      return;
    }

    setBookingSubmitting(true);

    try {
      const bookingData = {
        ...appointmentData,
        slot_id: selectedSlot.slot_id,
        date: formatDateString(selectedDate),
        time: selectedSlot.time
      };

      const response = await fetch(`${API}/appointments`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(bookingData)
      });

      if (response.ok) {
        toast.success('Rendez-vous réservé avec succès ! Vous recevrez une confirmation par email.');
        setAppointmentData({ client_name: '', client_email: '', client_phone: '', subject: '', message: '' });
        setSelectedSlot(null);
        setSelectedDate(null);
        setAvailableSlots([]);
      } else {
        throw new Error('Failed to book appointment');
      }
    } catch (error) {
      toast.error('Erreur lors de la réservation. Veuillez réessayer.');
    } finally {
      setBookingSubmitting(false);
    }
  };

  const navigateMonth = (direction) => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + direction, 1));
    setSelectedDate(null);
    setSelectedSlot(null);
    setAvailableSlots([]);
  };

  const renderCalendar = () => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    const days = daysInMonth(year, month);
    const firstDay = firstDayOfMonth(year, month);
    const cells = [];

    // Empty cells for days before the first day of the month
    for (let i = 0; i < firstDay; i++) {
      cells.push(<div key={`empty-${i}`} className="h-10"></div>);
    }

    // Days of the month
    for (let day = 1; day <= days; day++) {
      const date = new Date(year, month, day);
      const isPast = isDateInPast(date);
      const isWeekendDay = isWeekend(date);
      const hasSlots = hasAvailableSlots(date);
      const isSelected = selectedDate && 
        selectedDate.getDate() === day && 
        selectedDate.getMonth() === month && 
        selectedDate.getFullYear() === year;
      const isDisabled = isPast || isWeekendDay || !hasSlots;

      cells.push(
        <button
          key={day}
          onClick={() => !isDisabled && setSelectedDate(date)}
          disabled={isDisabled}
          className={`h-10 w-10 rounded-full flex items-center justify-center text-sm font-medium transition-all
            ${isSelected ? 'bg-[#006F33] text-white shadow-lg scale-110' : ''}
            ${hasSlots && !isPast && !isWeekendDay ? 'bg-[#006F33]/10 text-[#006F33] hover:bg-[#006F33] hover:text-white cursor-pointer' : ''}
            ${isDisabled ? 'text-slate-300 cursor-not-allowed' : ''}
          `}
          data-testid={`calendar-day-${day}`}
        >
          {day}
        </button>
      );
    }

    return cells;
  };

  return (
    <Layout>
      {/* Hero Section */}
      <section className="py-20 md:py-32 gradient-hero" data-testid="contact-hero">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="max-w-3xl">
            <span className="text-sm font-semibold text-[#006F33] uppercase tracking-wider">Contact</span>
            <h1 className="hero-title mt-4" data-testid="contact-title">
              Parlons de votre <span className="text-[#006F33]">projet</span>
            </h1>
            <p className="hero-subtitle mt-6">
              Contactez-nous pour une consultation gratuite ou réservez directement un rendez-vous.
            </p>
          </div>
        </div>
      </section>

      {/* Contact Info Cards */}
      <section className="py-12 bg-white border-b border-slate-100" data-testid="contact-info">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {contactInfo.map((info, index) => (
              <div key={index} className="flex items-start gap-4 p-6 bg-[#F9F9F7] rounded-xl" data-testid={`contact-info-${index}`}>
                <div className="w-12 h-12 rounded-xl bg-[#006F33]/10 flex items-center justify-center flex-shrink-0">
                  <info.icon className="w-6 h-6 text-[#006F33]" />
                </div>
                <div>
                  <h3 className="font-semibold text-[#1A1A1A]">{info.title}</h3>
                  {Array.isArray(info.content) ? (
                    <div className="flex flex-col gap-1">
                      {info.content.map((item, i) => (
                        <a key={i} href={info.links[i]} className="text-slate-600 hover:text-[#006F33] transition-colors">
                          {item}
                        </a>
                      ))}
                    </div>
                  ) : info.link ? (
                    <a href={info.link} className="text-slate-600 hover:text-[#006F33] transition-colors">
                      {info.content}
                    </a>
                  ) : (
                    <p className="text-slate-600">{info.content}</p>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Main Content: Contact Form & Calendar */}
      <section className="py-20 bg-[#F9F9F7]" data-testid="contact-forms">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Contact Form */}
            <div className="contact-form" data-testid="contact-form">
              <h2 className="text-2xl font-bold text-[#1A1A1A] mb-6">Envoyez-nous un message</h2>
              <form onSubmit={handleContactSubmit} className="space-y-6">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="name">Nom complet *</Label>
                    <Input
                      id="name"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      required
                      className="input-sunulex"
                      data-testid="contact-name"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="email">Email *</Label>
                    <Input
                      id="email"
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      required
                      className="input-sunulex"
                      data-testid="contact-email"
                    />
                  </div>
                </div>
                
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="phone">Téléphone</Label>
                    <Input
                      id="phone"
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      className="input-sunulex"
                      data-testid="contact-phone"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="subject">Sujet *</Label>
                    <Input
                      id="subject"
                      value={formData.subject}
                      onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                      required
                      className="input-sunulex"
                      data-testid="contact-subject"
                    />
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="message">Message *</Label>
                  <Textarea
                    id="message"
                    value={formData.message}
                    onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                    required
                    rows={5}
                    className="input-sunulex resize-none"
                    data-testid="contact-message"
                  />
                </div>
                
                <Button 
                  type="submit" 
                  className="btn-primary w-full flex items-center justify-center gap-2"
                  disabled={submitting}
                  data-testid="contact-submit"
                >
                  {submitting ? (
                    <div className="spinner w-5 h-5"></div>
                  ) : (
                    <>
                      <Send className="w-5 h-5" />
                      Envoyer le message
                    </>
                  )}
                </Button>
              </form>
            </div>

            {/* Calendar Booking */}
            <div className="contact-form" data-testid="booking-section">
              <h2 className="text-2xl font-bold text-[#1A1A1A] mb-2 flex items-center gap-2">
                <CalendarIcon className="w-6 h-6 text-[#006F33]" />
                Réservez un rendez-vous
              </h2>
              <p className="text-slate-500 mb-6">Sélectionnez une date (en vert) puis choisissez votre créneau horaire</p>
              
              {/* Calendar */}
              <div className="mb-8 bg-slate-50 rounded-xl p-4">
                <div className="flex items-center justify-between mb-4">
                  <button
                    onClick={() => navigateMonth(-1)}
                    className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
                    data-testid="calendar-prev"
                  >
                    <ChevronLeft className="w-5 h-5" />
                  </button>
                  <h3 className="font-semibold text-lg">
                    {monthNames[currentDate.getMonth()]} {currentDate.getFullYear()}
                  </h3>
                  <button
                    onClick={() => navigateMonth(1)}
                    className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
                    data-testid="calendar-next"
                  >
                    <ChevronRight className="w-5 h-5" />
                  </button>
                </div>
                
                {/* Day names */}
                <div className="grid grid-cols-7 gap-1 mb-2">
                  {dayNames.map((day) => (
                    <div key={day} className="h-10 flex items-center justify-center text-sm font-medium text-slate-500">
                      {day}
                    </div>
                  ))}
                </div>
                
                {/* Calendar grid */}
                <div className="grid grid-cols-7 gap-1" data-testid="calendar-grid">
                  {renderCalendar()}
                </div>
                
                {/* Legend */}
                <div className="flex items-center gap-4 mt-4 pt-4 border-t border-slate-200 text-xs">
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 rounded-full bg-[#006F33]/10"></div>
                    <span className="text-slate-600">Disponible</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 rounded-full bg-[#006F33]"></div>
                    <span className="text-slate-600">Sélectionné</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 rounded-full bg-slate-100"></div>
                    <span className="text-slate-600">Indisponible</span>
                  </div>
                </div>
              </div>

              {/* Time Slots */}
              {selectedDate && (
                <div className="mb-8" data-testid="time-slots">
                  <h4 className="font-semibold mb-4">
                    Créneaux disponibles le {selectedDate.toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' })}
                  </h4>
                  {loadingSlots ? (
                    <div className="flex justify-center py-4">
                      <div className="spinner w-8 h-8"></div>
                    </div>
                  ) : availableSlots.length === 0 ? (
                    <p className="text-slate-500 text-center py-4">Aucun créneau disponible pour cette date.</p>
                  ) : (
                    <div className="grid grid-cols-3 gap-2">
                      {availableSlots.map((slot) => (
                        <button
                          key={slot.slot_id}
                          onClick={() => setSelectedSlot(slot)}
                          disabled={!slot.available}
                          className={`py-3 rounded-lg font-medium transition-colors ${
                            selectedSlot?.slot_id === slot.slot_id
                              ? 'bg-[#006F33] text-white'
                              : slot.available
                              ? 'bg-slate-100 hover:bg-[#006F33]/10 text-slate-700'
                              : 'bg-slate-50 text-slate-300 cursor-not-allowed'
                          }`}
                          data-testid={`slot-${slot.time}`}
                        >
                          {slot.time}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Booking Form */}
              {selectedSlot && (
                <form onSubmit={handleBookingSubmit} className="space-y-4" data-testid="booking-form">
                  <div className="p-4 bg-[#006F33]/10 rounded-lg mb-4">
                    <p className="text-[#006F33] font-medium">
                      Rendez-vous le {selectedDate.toLocaleDateString('fr-FR')} à {selectedSlot.time}
                    </p>
                  </div>
                  
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Nom complet *</Label>
                      <Input
                        value={appointmentData.client_name}
                        onChange={(e) => setAppointmentData({ ...appointmentData, client_name: e.target.value })}
                        required
                        className="input-sunulex"
                        data-testid="booking-name"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Email *</Label>
                      <Input
                        type="email"
                        value={appointmentData.client_email}
                        onChange={(e) => setAppointmentData({ ...appointmentData, client_email: e.target.value })}
                        required
                        className="input-sunulex"
                        data-testid="booking-email"
                      />
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Téléphone *</Label>
                      <Input
                        value={appointmentData.client_phone}
                        onChange={(e) => setAppointmentData({ ...appointmentData, client_phone: e.target.value })}
                        required
                        className="input-sunulex"
                        data-testid="booking-phone"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Sujet *</Label>
                      <Input
                        value={appointmentData.subject}
                        onChange={(e) => setAppointmentData({ ...appointmentData, subject: e.target.value })}
                        required
                        className="input-sunulex"
                        data-testid="booking-subject"
                      />
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    <Label>Message (optionnel)</Label>
                    <Textarea
                      value={appointmentData.message}
                      onChange={(e) => setAppointmentData({ ...appointmentData, message: e.target.value })}
                      rows={3}
                      className="input-sunulex resize-none"
                      data-testid="booking-message"
                    />
                  </div>
                  
                  <Button 
                    type="submit" 
                    className="btn-primary w-full"
                    disabled={bookingSubmitting}
                    data-testid="booking-submit"
                  >
                    {bookingSubmitting ? (
                      <div className="spinner w-5 h-5"></div>
                    ) : (
                      'Confirmer le rendez-vous'
                    )}
                  </Button>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Map Section */}
      <section className="py-20 bg-white" data-testid="map-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="text-center mb-12">
            <h2 className="section-title">Nous trouver</h2>
            <p className="section-subtitle mt-4">Notre bureau est situé à Thiès, au Sénégal</p>
          </div>
          <div className="h-[400px] bg-slate-200 rounded-2xl overflow-hidden">
            <iframe
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d61765.75087694893!2d-16.969444!3d14.791944!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0xec114e11c09e9c5%3A0x5e8e6a84e3e8a9b0!2sThi%C3%A8s%2C%20Senegal!5e0!3m2!1sen!2sus!4v1700000000000!5m2!1sen!2sus"
              width="100%"
              height="100%"
              style={{ border: 0 }}
              allowFullScreen=""
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              title="Teranga Global Legal Location"
            ></iframe>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default ContactPage;
